import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { FaTruck, FaBox, FaCheckCircle, FaTimesCircle, FaMotorcycle, FaChartLine, FaShoppingCart, FaHourglassHalf, FaClock } from 'react-icons/fa';
import './BackendDashboard.css';

const BackendDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!user || !['admin', 'support'].includes(user.userType)) {
      navigate('/');
      return;
    }
    
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // SINGLE API CALL - Get everything in one request
      const response = await api.get('/backend/dashboard/stats');
      console.log('Dashboard data:', response.data);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const createDelivery = async (orderId) => {
    try {
      const order = dashboardData?.data?.ordersConfirmed?.find(o => o._id === orderId);
      if (!order) return;

      const deliveryData = {
        orderId: orderId,
        pickupAddress: {
          address: order.seller?.address?.fullAddress || 'Seller address',
          coordinates: [0, 0]
        },
        deliveryAddress: {
          address: order.buyer?.address?.fullAddress || 'Buyer address',
          coordinates: [0, 0]
        },
        estimatedDistance: 10,
        deliveryFee: 100
      };

      const response = await api.post('/backend/deliveries/create', deliveryData);
      toast.success(`Delivery created! Notified ${response.data.notifiedPartners} partners`);
      fetchDashboardData();
    } catch (error) {
      console.error('Error creating delivery:', error);
      toast.error(error.response?.data?.message || 'Failed to create delivery');
    }
  };

  const renderOrderCard = (order, showActions = false) => (
    <div key={order._id} className="order-card">
      <div className="order-header">
        <h3>Order #{order.orderNumber}</h3>
        <span className={`order-status status-${order.status}`}>{order.status}</span>
      </div>
      
      <div className="order-details">
        <div className="detail-row">
          <strong>Seller:</strong>
          <span>{order.seller?.businessName}</span>
        </div>
        <div className="detail-row">
          <strong>Buyer:</strong>
          <span>{order.buyer?.businessName}</span>
        </div>
        <div className="detail-row">
          <strong>Items:</strong>
          <span>{order.items?.length} items</span>
        </div>
        <div className="detail-row">
          <strong>Total:</strong>
          <span>₹{order.pricing?.total?.toFixed(2)}</span>
        </div>
        <div className="detail-row">
          <strong>Date:</strong>
          <span>{new Date(order.createdAt).toLocaleDateString()}</span>
        </div>
      </div>

      {showActions && order.status === 'confirmed' && (
        <div className="order-actions">
          <button
            className="btn btn-primary"
            onClick={() => createDelivery(order._id)}
          >
            <FaTruck /> Create Delivery
          </button>
        </div>
      )}
    </div>
  );

  const renderDeliveryCard = (delivery) => (
    <div key={delivery._id} className="delivery-card">
      <div className="delivery-header">
        <h3>Delivery #{delivery.deliveryNumber}</h3>
        <span className={`delivery-status status-${delivery.status}`}>
          {delivery.status.replace(/_/g, ' ')}
        </span>
      </div>
      <div className="delivery-details">
        <div className="detail-row">
          <strong>Order:</strong>
          <span>{delivery.order?.orderNumber || 'N/A'}</span>
        </div>
        <div className="detail-row">
          <strong>Seller:</strong>
          <span>{delivery.seller?.businessName || 'N/A'}</span>
        </div>
        <div className="detail-row">
          <strong>Buyer:</strong>
          <span>{delivery.buyer?.businessName || 'N/A'}</span>
        </div>
        <div className="detail-row">
          <strong>Fee:</strong>
          <span>₹{delivery.deliveryFee}</span>
        </div>
        <div className="detail-row">
          <strong>Distance:</strong>
          <span>{delivery.estimatedDistance} km</span>
        </div>
        {delivery.deliveryPartner && (
          <div className="detail-row">
            <strong>Partner:</strong>
            <span>{delivery.deliveryPartner.user?.businessName || 'N/A'}</span>
          </div>
        )}
        <div className="detail-row">
          <strong>Date:</strong>
          <span>{new Date(delivery.createdAt).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );

  const renderPartnerCard = (partner) => (
    <div key={partner._id} className="partner-card">
      <div className="partner-header">
        <h3>{partner.user?.businessName || 'Unknown Partner'}</h3>
        <span className={`partner-status status-${partner.availability?.status}`}>
          {partner.availability?.status || 'offline'}
        </span>
      </div>
      <div className="partner-details">
        <div className="detail-row">
          <strong>Email:</strong>
          <span>{partner.user?.email}</span>
        </div>
        <div className="detail-row">
          <strong>Phone:</strong>
          <span>{partner.user?.phone}</span>
        </div>
        <div className="detail-row">
          <strong>Vehicle:</strong>
          <span>{partner.vehicleType} - {partner.vehicleNumber}</span>
        </div>
        <div className="detail-row">
          <strong>Completed:</strong>
          <span>{partner.deliveryStats?.completedDeliveries || 0} deliveries</span>
        </div>
        <div className="detail-row">
          <strong>Rating:</strong>
          <span>⭐ {partner.deliveryStats?.averageRating?.toFixed(1) || 'N/A'}</span>
        </div>
        <div className="detail-row">
          <strong>Available:</strong>
          <span>{partner.availability?.isAvailable ? '✅ Yes' : '❌ No'}</span>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading dashboard...</div>
      </Layout>
    );
  }

  const counts = dashboardData?.counts || {};
  const data = dashboardData?.data || {};

  return (
    <Layout>
      <div className="backend-dashboard">
        <div className="dashboard-header">
          <h1>Backend Team Dashboard</h1>
          <p>Manage orders, deliveries, and delivery partners</p>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card orders">
            <div className="stat-icon">
              <FaBox />
            </div>
            <div className="stat-info">
              <h3>{counts.allOrders || 0}</h3>
              <p>Total Orders</p>
            </div>
          </div>

          <div className="stat-card deliveries">
            <div className="stat-icon">
              <FaTruck />
            </div>
            <div className="stat-info">
              <h3>{counts.allDeliveries || 0}</h3>
              <p>Total Deliveries</p>
            </div>
          </div>

          <div className="stat-card partners">
            <div className="stat-icon">
              <FaMotorcycle />
            </div>
            <div className="stat-info">
              <h3>{counts.allPartners || 0}</h3>
              <p>Total Partners</p>
            </div>
          </div>

          <div className="stat-card completed">
            <div className="stat-icon">
              <FaCheckCircle />
            </div>
            <div className="stat-info">
              <h3>{counts.deliveriesCompleted || 0}</h3>
              <p>Completed Deliveries</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="dashboard-tabs">
          <button
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <FaChartLine /> Overview
          </button>
          <button
            className={`tab ${activeTab === 'all-orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('all-orders')}
          >
            <FaShoppingCart /> All Orders ({counts.allOrders || 0})
          </button>
          <button
            className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            <FaClock /> Pending ({counts.ordersPending || 0})
          </button>
          <button
            className={`tab ${activeTab === 'confirmed' ? 'active' : ''}`}
            onClick={() => setActiveTab('confirmed')}
          >
            <FaCheckCircle /> Confirmed ({counts.ordersConfirmed || 0})
          </button>
          <button
            className={`tab ${activeTab === 'processing' ? 'active' : ''}`}
            onClick={() => setActiveTab('processing')}
          >
            <FaHourglassHalf /> Processing ({counts.ordersProcessing || 0})
          </button>
          <button
            className={`tab ${activeTab === 'shipped' ? 'active' : ''}`}
            onClick={() => setActiveTab('shipped')}
          >
            <FaTruck /> Shipped ({counts.ordersShipped || 0})
          </button>
          <button
            className={`tab ${activeTab === 'delivered' ? 'active' : ''}`}
            onClick={() => setActiveTab('delivered')}
          >
            <FaCheckCircle /> Delivered ({counts.ordersDelivered || 0})
          </button>
          <button
            className={`tab ${activeTab === 'all-deliveries' ? 'active' : ''}`}
            onClick={() => setActiveTab('all-deliveries')}
          >
            <FaTruck /> All Deliveries ({counts.allDeliveries || 0})
          </button>
          <button
            className={`tab ${activeTab === 'partners' ? 'active' : ''}`}
            onClick={() => setActiveTab('partners')}
          >
            <FaMotorcycle /> All Partners ({counts.allPartners || 0})
          </button>
        </div>

        {/* Content */}
        <div className="dashboard-content">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="overview-section">
              <h2>Dashboard Overview</h2>
              <div className="overview-stats">
                <div className="overview-card">
                  <h3>{counts.allOrders || 0}</h3>
                  <p>Total Orders</p>
                </div>
                <div className="overview-card">
                  <h3>{counts.ordersPending || 0}</h3>
                  <p>Pending Orders</p>
                </div>
                <div className="overview-card">
                  <h3>{counts.ordersConfirmed || 0}</h3>
                  <p>Confirmed Orders</p>
                </div>
                <div className="overview-card">
                  <h3>{counts.ordersShipped || 0}</h3>
                  <p>Shipped Orders</p>
                </div>
                <div className="overview-card">
                  <h3>{counts.allDeliveries || 0}</h3>
                  <p>Total Deliveries</p>
                </div>
                <div className="overview-card">
                  <h3>{counts.allPartners || 0}</h3>
                  <p>Total Partners</p>
                </div>
              </div>
            </div>
          )}

          {/* All Orders Tab */}
          {activeTab === 'all-orders' && (
            <div className="orders-section">
              <h2>All Orders</h2>
              {!data.allOrders || data.allOrders.length === 0 ? (
                <div className="empty-state">
                  <FaShoppingCart />
                  <p>No orders found</p>
                </div>
              ) : (
                <div className="orders-list">
                  {data.allOrders.map(order => renderOrderCard(order, false))}
                </div>
              )}
            </div>
          )}

          {/* Pending Orders Tab */}
          {activeTab === 'pending' && (
            <div className="orders-section">
              <h2>Pending Orders</h2>
              {!data.ordersPending || data.ordersPending.length === 0 ? (
                <div className="empty-state">
                  <FaClock />
                  <p>No pending orders</p>
                </div>
              ) : (
                <div className="orders-list">
                  {data.ordersPending.map(order => renderOrderCard(order, false))}
                </div>
              )}
            </div>
          )}

          {/* Confirmed Orders Tab */}
          {activeTab === 'confirmed' && (
            <div className="orders-section">
              <h2>Confirmed Orders</h2>
              {!data.ordersConfirmed || data.ordersConfirmed.length === 0 ? (
                <div className="empty-state">
                  <FaCheckCircle />
                  <p>No confirmed orders</p>
                </div>
              ) : (
                <div className="orders-list">
                  {data.ordersConfirmed.map(order => renderOrderCard(order, true))}
                </div>
              )}
            </div>
          )}

          {/* Processing Orders Tab */}
          {activeTab === 'processing' && (
            <div className="orders-section">
              <h2>Processing Orders</h2>
              {!data.ordersProcessing || data.ordersProcessing.length === 0 ? (
                <div className="empty-state">
                  <FaHourglassHalf />
                  <p>No orders being processed</p>
                </div>
              ) : (
                <div className="orders-list">
                  {data.ordersProcessing.map(order => renderOrderCard(order, false))}
                </div>
              )}
            </div>
          )}

          {/* Shipped Orders Tab */}
          {activeTab === 'shipped' && (
            <div className="orders-section">
              <h2>Shipped Orders</h2>
              {!data.ordersShipped || data.ordersShipped.length === 0 ? (
                <div className="empty-state">
                  <FaTruck />
                  <p>No shipped orders</p>
                </div>
              ) : (
                <div className="orders-list">
                  {data.ordersShipped.map(order => renderOrderCard(order, false))}
                </div>
              )}
            </div>
          )}

          {/* Delivered Orders Tab */}
          {activeTab === 'delivered' && (
            <div className="orders-section">
              <h2>Delivered Orders</h2>
              {!data.ordersDelivered || data.ordersDelivered.length === 0 ? (
                <div className="empty-state">
                  <FaCheckCircle />
                  <p>No delivered orders</p>
                </div>
              ) : (
                <div className="orders-list">
                  {data.ordersDelivered.map(order => renderOrderCard(order, false))}
                </div>
              )}
            </div>
          )}

          {/* All Deliveries Tab */}
          {activeTab === 'all-deliveries' && (
            <div className="deliveries-section">
              <h2>All Deliveries</h2>
              {!data.allDeliveries || data.allDeliveries.length === 0 ? (
                <div className="empty-state">
                  <FaTruck />
                  <p>No deliveries found</p>
                  <small>Create a delivery from a confirmed order to see it here</small>
                </div>
              ) : (
                <div className="deliveries-list">
                  {data.allDeliveries.map(delivery => renderDeliveryCard(delivery))}
                </div>
              )}
            </div>
          )}

          {/* Partners Tab */}
          {activeTab === 'partners' && (
            <div className="partners-section">
              <h2>All Delivery Partners</h2>
              {!data.allPartners || data.allPartners.length === 0 ? (
                <div className="empty-state">
                  <FaMotorcycle />
                  <p>No delivery partners registered</p>
                  <small>Partners need to register with userType: "delivery_partner"</small>
                </div>
              ) : (
                <div className="partners-list">
                  {data.allPartners.map(partner => renderPartnerCard(partner))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default BackendDashboard;
